import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Button,
  Snackbar,
  Checkbox,
  FormControlLabel,
  TextField,
  Grid2,
} from "@mui/material";
import { useAppDispatch } from "../../redux/hooks";
import Grid from "@mui/material/Grid2";
import { useLocation, useNavigate } from "react-router-dom";

import editIcon from "../../assets/edit-pencil-icon.png";
import { CustomerEndPoints } from "../../constants/endpoints";
import {
  commonButtonStyles,
  commonPrimaryButtonStyles,
  displayFlexJustifyEnd,
  editIconStyles,
  creditLimitDisabledStyle,
} from "./CustomerDetailsStyles";
import Breadcrumbs from "../../common/Breadcrumbs";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { step1Fields, step2Fields, step3Fields } from "./steps";
import {
  customerDetailsStep1,
  customerDetailsStep2,
  customerDetailsStep3,
} from "../../schemas/customerDetails";
import { yupResolver } from "@hookform/resolvers/yup";
import { CustomerStep } from "./CustomerStep";
import {
  checkBox,
  checkBoxChecked,
  fieldTitleStyle,
  titleStyle,
} from "./customerStepStyles";
import { Switch } from "../../common/Switch";
import {
  customerDetailsStep1Defaults,
  customerDetailsStep2Defaults,
  customerDetailsStep3Defaults,
} from "../../constants/formDefaultValues/customerDetailsDefaultValues";
import { createPayload } from "./createPayload";
import getPopulatedValues from "./populateDefaultValues";
import checkIcon from "../../assets/checkBox_check.svg";
import Toast from "../../common/Toast";
import { setLoading } from "../../redux/features/slices/appSlice";
import {
  deleteCustomer,
  createNewCustomer,
  editCustomer,
  getPresignedUrls,
  getCustomerById,
  uploadFileToS3Api,
} from "../../api/customerAPI";
import bucketMapping from "../../constants/S3BuketMapping";

const CheckIcon = () => (
  <img src={checkIcon} alt="check-icon" height="8px" width="11px" />
);

const CustomerDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isCreateNewCustomer = location.pathname.includes("create-new-customer");
  const userID = isCreateNewCustomer
    ? undefined
    : location.pathname.split("/").pop();

  const [userData, setUserData] = useState<
    | {
        step1: any;
        step2: any;
        step3: any;
      }
    | undefined
  >();

  const dispatch = useAppDispatch();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [error, setError] = useState(false);

  const [loadingData, setLoadingData] = useState(!isCreateNewCustomer);
  const [isEditable, setIsEditable] = useState(isCreateNewCustomer);

  const [step1DefaultValues, setStep1DefaultValues] = useState(
    customerDetailsStep1Defaults
  );
  const [step2DefaultValues, setStep2DefaultValues] = useState(
    customerDetailsStep2Defaults
  );
  const [step3DefaultValues, setStep3DefaultValues] = useState(
    customerDetailsStep3Defaults
  );

  const [defaultAdvancedPayment, setDefaultAdvancedPayment] = useState(false);

  const orgId = localStorage.getItem("orgId");

  useEffect(() => {
    if (!isCreateNewCustomer) {
      const fetchUserData = async () => {
        try {
          const response = await getCustomerById(userID);
          if (!response) {
            console.error("Failed to fetch customer data");
          } else {
            console.log("Fetched customer data:", response);
          }
          console.log(response, "resp");
          const values = getPopulatedValues(response.data.data);
          setUserData(values);

          setDefaultAdvancedPayment(values.advancePaymentMandatory);
          setAdvanceMandatory(values.advancePaymentMandatory);
          setDefaultEmailActivation(values.emailActivation);
          setEmailActivation(values.emailActivation);

          setStep1DefaultValues(values.step1);
          setStep2DefaultValues(values.step2);
          setStep3DefaultValues(values.step3);

          step1Control.reset(values.step1);
          step2Control.reset(values.step2);
          step3Control.reset(values.step3);
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setLoadingData(false);
        }
      };
      fetchUserData();
    }
  }, [userID]);

  const uploadMultipleFilesWithPresignedUrls = async (
    presignedUrls: { presignedUrl: string; key: string; fileName: string }[],
    documents: ({
      fileContent: File | Blob;
      fileType: string;
      documentType: string;
      fileName: string;
    } | null)[]
  ) => {
    // Filter out null documents before processing
    const validDocuments = documents.filter(
      (
        doc
      ): doc is {
        fileContent: File | Blob;
        fileType: string;
        documentType: string;
        fileName: string;
      } => doc !== null && doc.fileName.trim() !== ""
    );

    dispatch(setLoading(true));
    const uploadedDocuments = await Promise.all(
      presignedUrls.map(async (presignedFile, index) => {
        const document = validDocuments[index]; // Ensure correct mapping

        if (!document) {
          console.warn(
            `Skipping upload: No document found for ${presignedFile.fileName}`
          );
          return null;
        }

        const isUploaded = await uploadSingleFileWithPresignedUrl(
          presignedFile.presignedUrl,
          document.fileContent,
          document.fileType
        );

        if (isUploaded) {
          return {
            documentId: presignedFile.presignedUrl, // S3 URL
            type: document.documentType,
            fileName: document.fileName,
            key: presignedFile.key, // S3 key
          };
        } else {
          throw new Error(`Upload failed for ${document.fileName}`);
        }
      })
    );
    dispatch(setLoading(false));

    // Remove null values before returning
    return uploadedDocuments.filter((doc) => doc !== null);
  };

  const step1Control = useForm({
    resolver: yupResolver(customerDetailsStep1),
    defaultValues: step1DefaultValues,
    mode: "onBlur",
  });

  useEffect(() => {
    if (isCreateNewCustomer) {
      step1Control.setValue("comp_vat_cert", "");
      step2Control.setValue("lic_gst_cert", "");
    }
  }, []);

  const step2Control = useForm({
    resolver: yupResolver(customerDetailsStep2),
    defaultValues: step2DefaultValues,
    mode: "onBlur",
  });

  const step3Control = useForm({
    resolver: yupResolver(customerDetailsStep3),
    defaultValues: step3DefaultValues,
    mode: "onBlur",
  });

  const { control, setValue, trigger, clearErrors } = useForm({
    defaultValues: {
      creditLimit: "",
    },
    mode: "onBlur",
  });

  const validateAllSteps = async () => {
    const step1IsValid = await step1Control.trigger();
    const step2IsValid = await step2Control.trigger();
    const step3IsValid = await step3Control.trigger();
    const creditLimitIsValid = await trigger("creditLimit");

    return step1IsValid && step2IsValid && step3IsValid && creditLimitIsValid;
  };

  const [advanceMandatory, setAdvanceMandatory] = useState(false);

  const [showEditForm, setShowEditForm] = useState(false);

  const [emailActivation, setEmailActivation] = useState(false);
  const [defaultEmailActivation, setDefaultEmailActivation] = useState(false);

  const handleReset = () => {
    step1Control.reset(step1DefaultValues);
    step2Control.reset(step2DefaultValues);
    step3Control.reset(step3DefaultValues);
    setAdvanceMandatory(defaultAdvancedPayment);
    setEmailActivation(defaultEmailActivation);
  };

  const uploadSingleFileWithPresignedUrl = async (
    presignedUrl: string,
    binaryData: File | Blob,
    fileType: string
  ) => {
    try {
      const blobData =
        binaryData instanceof Blob
          ? binaryData
          : new Blob([binaryData], { type: fileType });
      const response: any = await uploadFileToS3Api(
        presignedUrl,
        blobData,
        fileType
      );

      return response;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw new Error("File upload failed");
    }
  };

  type ValidDocument = {
    fileName: string;
    fileContent: string;
    documentType: string;
    fileType: string;
  };

  const extractValidDocuments = (
    documents: (ValidDocument | null)[]
  ): Omit<ValidDocument, "fileContent">[] => {
    return documents
      ?.filter(
        (doc): doc is ValidDocument =>
          !!doc &&
          typeof doc.fileName === "string" &&
          doc.fileName.trim() !== "" &&
          typeof doc.fileType === "string" &&
          typeof doc.documentType === "string"
      )
      .map(({ fileName, fileType, documentType }) => ({
        fileName,
        fileType,
        documentType,
      }));
  };

  const handleSubmit = async () => {
    setError(false);
    if (isCreateNewCustomer) {
      const isValid = await validateAllSteps();
      const formData = {
        company: step1Control.getValues(),
        license: step2Control.getValues(),
        bank: step3Control.getValues(),
        advancePaymentMandatory: advanceMandatory,
        emailActivation: emailActivation,
      };

      const payload = await createPayload(formData);

      if (isValid) {
        try {
          const updatedPayload = extractValidDocuments(payload.documents);

          const presignedurls =
            (await getPresignedUrls(updatedPayload, bucketMapping.customer)) ||
            [];
          const finalDocumentData: any[] =
            await uploadMultipleFilesWithPresignedUrls(
              presignedurls,
              payload.documents
            );
          await createNewCustomer(payload, finalDocumentData);
          setToastMessage("Customer created successfully");
          setTimeout(() => {
            navigate(`/${orgId}/seller/all-customers`);
          }, 2000);
        } catch (error: any) {
          setError(true);
          if (
            error?.response?.data?.message ===
            `Customer with email ${payload?.customer?.emailId} already exists.`
          ) {
            setToastMessage("The email ID already exists.");
          } else {
            setToastMessage("Something went wrong");
          }

          console.error("Error creating Customer:", error);
        }
        setShowToast(true);
        setShowEditForm(false);
      }
    } else {
      if (isEditable) {
        const isValid = await validateAllSteps();
        const formData = {
          company: step1Control.getValues(),
          license: step2Control.getValues(),
          bank: step3Control.getValues(),
          advancePaymentMandatory: advanceMandatory,
          emailActivation: emailActivation,
        };

        const oldDocuments = [
          step1DefaultValues.comp_gst_cert,
          step1DefaultValues.comp_pan_card,
          step1DefaultValues.comp_vat_cert,
          step2DefaultValues.lic_rvb_license,
          step2DefaultValues.lic_license_pan,
          step2DefaultValues.lic_gst_cert,
          step3DefaultValues.bank_cancelled_cheque,
        ];

        const payload = await createPayload(formData);

        const deletedDocuments = oldDocuments.filter((doc: any) => {
          return payload.documents.find(
            (newDoc: any) =>
              newDoc.documentType === doc?.originalObj?.type &&
              newDoc.fileName === ""
          );
        });

        const documents = {
          oldDocuments: oldDocuments,
          newDocuments: payload.documents.filter((doc: any) => doc.fileContent),
          deletedDocuments: deletedDocuments.map((doc: any) => doc.originalObj),
        };

        // **2. Upload New Documents to S3**
        let uploadedDocuments: any[] = [];
        if (documents.newDocuments.length > 0) {
          const updatedPayload = extractValidDocuments(payload.documents);
          const presignedurls =
            (await getPresignedUrls(updatedPayload, bucketMapping.customer)) ||
            [];

          uploadedDocuments = await uploadMultipleFilesWithPresignedUrls(
            presignedurls,
            documents.newDocuments
          );
        }

        // **3. Update Old Documents or Add New Ones**
        const finalNewDocumentData: any[] = [];
        for (const document of documents.newDocuments) {
          const uploadedFile = uploadedDocuments.find(
            (item) => item.fileName === document?.fileName
          );
          const url = uploadedFile?.documentId;
          const Key = uploadedFile?.key;

          if (!Key || !url) {
            throw new Error(
              `Missing S3 upload data for file: ${document?.fileName}`
            );
          }
          const documentsData = {
            documentId: url,
            type: document?.documentType,
            fileName: document?.fileName,
            key: Key,
          };
          finalNewDocumentData.push(documentsData);
        }

        documents.newDocuments = finalNewDocumentData;

        const updatePayload = {
          ...payload,
          documents: documents,
        };

        if (isValid) {
          try {
            if (userID) {
              await editCustomer(userID, updatePayload);
            }
            setToastMessage("Customer updated successfully");
            setTimeout(() => {
              navigate(`/${orgId}/seller/all-customers`);
            }, 2000);
          } catch (error) {
            setToastMessage("Failed to update customer details");
            setError(true);
            console.error("Error updating Customer:", error);
          }
          setShowToast(true);
          setShowEditForm(false);
        }
      } else {
        try {
          let response;
          if (userID) {
            response = await deleteCustomer(userID, userData?.step1.emailID);
          }

          if (response?.status === 200) {
            setToastMessage("Customer deleted successfully");
            setTimeout(() => {
              navigate(`/${orgId}/seller/all-customers`);
            }, 2000);
          }
        } catch (error) {
          setToastMessage("Failed to delete customer");
          setError(true);
          console.error("Error deleting Customer:", error);
        }
        setShowToast(true);
      }
    }
  };

  const handleEditClick = () => {
    setIsEditable(true);
    setShowEditForm(true);
  };

  const breadcrumbPaths = ["All Customers", "Customer Details"];
  const breadcrumbLinks = [
    "/seller/all-customers",
    "/seller/create-new-customer",
  ];

  const handleAdvanceMandatoryChange = () => {
    if (isCreateNewCustomer || isEditable) {
      setAdvanceMandatory(!advanceMandatory);
      if (!advanceMandatory) {
        setValue("creditLimit", "");
        clearErrors("creditLimit");
      }
    }
  };

  const handleEmailActivationChange = () => {
    setEmailActivation(!emailActivation);
  };

  const creditLimitValidation = {
    required: !advanceMandatory ? "Credit limit is required" : false,
    pattern: {
      value: /^\d+(\.\d{1,2})?$/,
      message: "Invalid input: Please enter a numeric value",
    },
  };

  return (
    <>
      <Breadcrumbs paths={breadcrumbPaths} links={breadcrumbLinks} />

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />

      <Box>
        <Card sx={{ padding: "20px 81px 18px", boxShadow: 2 }}>
          <CardContent sx={{ padding: 0 }}>
            <Grid
              size={6}
              sx={{
                ...displayFlexJustifyEnd,
              }}
            >
              {!showEditForm && !isCreateNewCustomer && (
                <img
                  src={editIcon}
                  onClick={handleEditClick}
                  alt="edit-icon"
                  style={editIconStyles}
                />
              )}
            </Grid>

            {!loadingData ? (
              <>
                {!isCreateNewCustomer && !userData?.step1 ? (
                  <Typography>
                    No customer data found for the id {userID}
                  </Typography>
                ) : (
                  <>
                    <FormProvider {...step1Control}>
                      <CustomerStep
                        control={step1Control.control}
                        fields={step1Fields}
                        title="STEP 1: Company Information"
                        disabled={!isCreateNewCustomer && !isEditable}
                        isFirstTitle={true}
                      />
                    </FormProvider>

                    <FormProvider {...step2Control}>
                      <CustomerStep
                        control={step2Control.control}
                        fields={step2Fields}
                        title="STEP 2: License Information"
                        disabled={!isCreateNewCustomer && !isEditable}
                        isFirstTitle={false}
                      />
                    </FormProvider>

                    <FormProvider {...step3Control}>
                      <CustomerStep
                        control={step3Control.control}
                        fields={step3Fields}
                        title="STEP 3: Bank Information"
                        disabled={!isCreateNewCustomer && !isEditable}
                        isFirstTitle={false}
                      />
                    </FormProvider>

                    {/* Advance Payment and Credit Limit */}
                    <Grid2 container spacing={2}>
                      <Grid2 size={[12, 6, 4]}>
                        <Typography sx={titleStyle}>
                          Advance Payment Mandatory
                        </Typography>
                        <Box>
                          <Switch
                            checked={advanceMandatory}
                            onChange={handleAdvanceMandatoryChange}
                          />
                        </Box>
                      </Grid2>
                      {/* <Grid2 size={[12, 6, 4]}>
                        <Typography sx={titleStyle}>Credit Limit</Typography>
                        <Controller
                          name="creditLimit"
                          control={control}
                          rules={creditLimitValidation}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              variant="outlined"
                              fullWidth
                              placeholder="Credit Limit"
                              disabled={advanceMandatory}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              sx={
                                advanceMandatory ? creditLimitDisabledStyle : {}
                              }
                            />
                          )}
                        />
                      </Grid2> */}
                    </Grid2>

                    <Box display={"flex"} alignItems={"center"} mt={"38px"}>
                      <FormControlLabel
                        disabled={
                          defaultEmailActivation ||
                          (!isCreateNewCustomer && !isEditable)
                        }
                        control={
                          <Checkbox
                            checked={emailActivation}
                            onChange={handleEmailActivationChange}
                            icon={<Box sx={checkBox} />}
                            checkedIcon={
                              <Box sx={checkBoxChecked}>
                                <CheckIcon />
                              </Box>
                            }
                          />
                        }
                        label={
                          <Typography sx={[titleStyle, { m: 0 }]}>
                            Activation Email
                          </Typography>
                        }
                        sx={{ display: "flex", alignItems: "center" }}
                      />
                    </Box>

                    <Box
                      component="form"
                      onSubmit={(e: any) => e.preventDefault()}
                    >
                      <Divider
                        sx={{ marginBottom: "36px", marginTop: "60px" }}
                      />

                      <Box>
                        <Grid container spacing={2}>
                          <Grid size={[0, 6]} />

                          <Grid
                            size={[12, 6, 3]}
                            sx={{
                              ...displayFlexJustifyEnd,
                            }}
                          >
                            {!isCreateNewCustomer && isEditable && (
                              <Button
                                variant="outlined"
                                style={commonButtonStyles}
                                onClick={handleReset}
                              >
                                Reset
                              </Button>
                            )}
                          </Grid>
                          <Grid
                            size={[12, 6, 3]}
                            sx={{
                              ...displayFlexJustifyEnd,
                            }}
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              sx={commonPrimaryButtonStyles}
                              onClick={handleSubmit}
                            >
                              {isCreateNewCustomer
                                ? "Create"
                                : isEditable
                                  ? "Save"
                                  : "Delete"}
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </>
                )}
              </>
            ) : (
              <Typography>Loading...</Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default CustomerDetails;
